import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Trash2, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { LEAVE_TYPES, LEAVE_STATUS } from '../../lib/leaveLabels';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const empty = (ctx) => ({ request_type: 'absence', start_date: ctx?.date || '', end_date: ctx?.date || '', reason: '', approve: true });

/** ctx = { person, date } pour un ajout, ou { person, req } pour une absence existante */
export const AbsenceEditDialog = ({ ctx, onClose, onSaved }) => {
  const req = ctx?.req;
  const [form, setForm] = useState(empty(ctx));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!ctx) return;
    setForm(req ? { request_type: req.request_type, start_date: req.start_date, end_date: req.end_date || req.start_date, reason: req.reason || '', approve: true } : empty(ctx));
  }, [ctx, req]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onSaved(); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setBusy(false); }
  };
  const payload = { ...form, user_id: ctx?.person?.id, end_date: form.request_type === 'advance_day' ? null : form.end_date || null };
  const save = () => run(() => (req ? api.put(`/leave-requests/${req.id}`, payload) : api.post('/leave-requests/manage', payload)), req ? 'Absence modifiée' : 'Absence ajoutée');
  const remove = () => window.confirm('Supprimer cette absence ?') && run(() => api.delete(`/leave-requests/${req.id}`), 'Absence supprimée');
  const decide = (d) => run(() => api.post(`/leave-requests/${req.id}/${d}`, { manager_note: '' }), d === 'approve' ? 'Demande validée' : 'Demande refusée');
  const S = req && (LEAVE_STATUS[req.status] || LEAVE_STATUS.pending);

  return (
    <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="absence-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#2B3A2C] flex items-center gap-2">{req ? 'Modifier l\'absence' : 'Ajouter une absence'}{S && <Badge className={`border ${S.className}`}>{S.label}</Badge>}</DialogTitle>
          <DialogDescription>{ctx?.person?.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">Type</Label>
            <Select value={form.request_type} onValueChange={(v) => set('request_type', v)}>
              <SelectTrigger data-testid="absence-edit-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(LEAVE_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs font-semibold">Du</Label><Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} data-testid="absence-edit-start" /></div>
            {form.request_type !== 'advance_day' && <div><Label className="text-xs font-semibold">Au</Label><Input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} data-testid="absence-edit-end" /></div>}
          </div>
          <div><Label className="text-xs font-semibold">{form.request_type === 'other' ? 'Objet *' : 'Motif'}</Label><Textarea rows={2} value={form.reason} onChange={(e) => set('reason', e.target.value)} data-testid="absence-edit-reason" /></div>
          {!req && (
            <label className="flex items-center gap-2 text-sm text-[#2B3A2C]">
              <input type="checkbox" checked={form.approve} onChange={(e) => set('approve', e.target.checked)} className="accent-[#B85C38] w-4 h-4" data-testid="absence-edit-approve" />
              Valider directement (sinon reste en attente)
            </label>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button disabled={busy} onClick={save} className="bg-[#B85C38] hover:bg-[#8F4426] text-white" data-testid="absence-edit-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Enregistrer
            </Button>
            {req?.status === 'pending' && <>
              <Button disabled={busy} onClick={() => decide('approve')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="absence-edit-approve-btn"><CheckCircle2 className="w-4 h-4 mr-1" />Valider</Button>
              <Button disabled={busy} variant="outline" onClick={() => decide('reject')} className="border-rose-400 text-rose-700" data-testid="absence-edit-reject-btn"><XCircle className="w-4 h-4 mr-1" />Refuser</Button>
            </>}
            {req && <Button disabled={busy} variant="ghost" onClick={remove} className="text-red-600 hover:bg-red-50 ml-auto" data-testid="absence-edit-delete"><Trash2 className="w-4 h-4 mr-1" />Supprimer</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AbsenceEditDialog;
